/**
 * WebhooksSettings - Manage webhook integrations
 * Users can register URLs to receive real-time lead notifications
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import PanelLayout from '@/components/PanelLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Webhook, Plus, Trash2, Play, RefreshCw, Copy, Eye, EyeOff,
  CheckCircle, XCircle, Clock, Loader2, ExternalLink, Zap,
  AlertTriangle, Settings
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

const EVENT_TYPES = [
  { id: 'lead.created', label: 'Nowy lead', description: 'Gdy ktoś wysyła zapytanie' },
  { id: 'lead.status_changed', label: 'Zmiana statusu', description: 'Gdy zmienia się status leada' },
  { id: 'lead.note_added', label: 'Nowa notatka', description: 'Gdy dodano notatkę do leada' },
];

const STATUS_CONFIG = {
  active: { label: 'Aktywny', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  paused: { label: 'Wstrzymany', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  failed: { label: 'Błąd', color: 'bg-red-100 text-red-700', icon: XCircle },
};

export default function WebhooksSettings() {
  const { t } = useTranslation();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeliveriesDialog, setShowDeliveriesDialog] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [showSecrets, setShowSecrets] = useState({});
  
  // Form state
  const [newUrl, setNewUrl] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [selectedEvents, setSelectedEvents] = useState(['lead.created']);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/webhooks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWebhooks(res.data);
    } catch (error) {
      toast.error('Błąd ładowania webhooków');
    } finally {
      setLoading(false);
    }
  };

  const createWebhook = async () => {
    if (!newUrl.trim()) {
      toast.error('Podaj URL webhooka');
      return;
    }
    if (selectedEvents.length === 0) {
      toast.error('Wybierz przynajmniej jedno zdarzenie');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/webhooks`, {
        url: newUrl,
        events: selectedEvents,
        description: newDescription
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setWebhooks(prev => [...prev, res.data]);
      setShowAddDialog(false);
      setNewUrl('');
      setNewDescription('');
      setSelectedEvents(['lead.created']);
      toast.success('Webhook utworzony!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd tworzenia webhooka');
    } finally {
      setSaving(false);
    }
  };

  const deleteWebhook = async (id) => {
    if (!confirm('Czy na pewno usunąć ten webhook?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/webhooks/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWebhooks(prev => prev.filter(w => w.id !== id));
      toast.success('Webhook usunięty');
    } catch (error) {
      toast.error('Błąd usuwania');
    }
  };

  const testWebhook = async (webhook) => {
    setTesting(prev => ({ ...prev, [webhook.id]: true }));
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/webhooks/${webhook.id}/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        toast.success(`Test OK! Status: ${res.data.status_code}, Czas: ${res.data.duration_ms}ms`);
      } else {
        toast.error(`Test nieudany: ${res.data.error}`);
      }
    } catch (error) {
      toast.error('Błąd testu');
    } finally {
      setTesting(prev => ({ ...prev, [webhook.id]: false }));
    }
  };

  const toggleWebhookStatus = async (webhook) => {
    const newStatus = webhook.status === 'active' ? 'paused' : 'active';
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/webhooks/${webhook.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWebhooks(prev => prev.map(w => 
        w.id === webhook.id ? { ...w, status: newStatus } : w
      ));
      toast.success(newStatus === 'active' ? 'Webhook aktywowany' : 'Webhook wstrzymany');
    } catch (error) {
      toast.error('Błąd zmiany statusu');
    }
  };

  const viewDeliveries = async (webhook) => {
    setSelectedWebhook(webhook);
    setShowDeliveriesDialog(true);
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/webhooks/${webhook.id}/deliveries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeliveries(res.data.deliveries);
    } catch (error) {
      toast.error('Błąd ładowania historii');
    }
  };

  const regenerateSecret = async (webhook) => {
    if (!confirm('Czy na pewno wygenerować nowy secret? Stary przestanie działać.')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/webhooks/${webhook.id}/regenerate-secret`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWebhooks(prev => prev.map(w => 
        w.id === webhook.id ? { ...w, secret: res.data.secret } : w
      ));
      toast.success('Nowy secret wygenerowany');
    } catch (error) {
      toast.error('Błąd generowania');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} skopiowano`);
  };

  if (loading) {
    return (
      <PanelLayout title="Webhooks">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#F59E0B] animate-spin" />
        </div>
      </PanelLayout>
    );
  }

  return (
    <PanelLayout title="Webhooks" subtitle="Integracje z zewnętrznymi systemami">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-zinc-500 dark:text-zinc-400">
              Otrzymuj powiadomienia o nowych leadach w czasie rzeczywistym.
              Integruj z Zapier, Make, własnym CRM.
            </p>
          </div>
          <Button 
            onClick={() => setShowAddDialog(true)}
            className="bg-[#F59E0B] hover:bg-[#D97706] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Dodaj webhook
          </Button>
        </div>

        {/* Webhooks List */}
        {webhooks.length === 0 ? (
          <div className="text-center py-16 bg-white dark:bg-gray-900 rounded-xl border border-zinc-200 dark:border-gray-800">
            <Webhook className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold mb-2">Brak webhooków</h3>
            <p className="text-zinc-500 mb-6 max-w-md mx-auto">
              Dodaj webhook, aby otrzymywać powiadomienia o nowych leadach 
              bezpośrednio do swojego systemu CRM lub Zapier.
            </p>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Dodaj pierwszy webhook
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {webhooks.map((webhook) => {
              const status = STATUS_CONFIG[webhook.status] || STATUS_CONFIG.active;
              const StatusIcon = status.icon;
              
              return (
                <motion.div
                  key={webhook.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white dark:bg-gray-900 rounded-xl border border-zinc-200 dark:border-gray-800 p-5"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <Badge className={status.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {status.label}
                        </Badge>
                        {webhook.failure_count > 0 && (
                          <Badge variant="outline" className="text-red-500 border-red-200">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {webhook.failure_count} błędów
                          </Badge>
                        )}
                      </div>
                      
                      <p className="font-mono text-sm text-zinc-700 dark:text-gray-300 truncate mb-2">
                        {webhook.url}
                      </p>
                      
                      {webhook.description && (
                        <p className="text-sm text-zinc-500 mb-2">{webhook.description}</p>
                      )}
                      
                      <div className="flex flex-wrap gap-2 mb-3">
                        {webhook.events.map(event => (
                          <Badge key={event} variant="outline" className="text-xs">
                            {EVENT_TYPES.find(e => e.id === event)?.label || event}
                          </Badge>
                        ))}
                      </div>
                      
                      {/* Secret */}
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-zinc-400">Secret:</span>
                        <code className="bg-zinc-100 dark:bg-gray-800 px-2 py-1 rounded font-mono">
                          {showSecrets[webhook.id] ? webhook.secret : '••••••••••••••••'}
                        </code>
                        <button 
                          onClick={() => setShowSecrets(prev => ({ ...prev, [webhook.id]: !prev[webhook.id] }))}
                          className="text-zinc-400 hover:text-zinc-600"
                        >
                          {showSecrets[webhook.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button 
                          onClick={() => copyToClipboard(webhook.secret, 'Secret')}
                          className="text-zinc-400 hover:text-zinc-600"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => testWebhook(webhook)}
                        disabled={testing[webhook.id]}
                      >
                        {testing[webhook.id] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewDeliveries(webhook)}
                      >
                        <Clock className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleWebhookStatus(webhook)}
                      >
                        {webhook.status === 'active' ? (
                          <Clock className="w-4 h-4" />
                        ) : (
                          <Zap className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-500 hover:text-red-600"
                        onClick={() => deleteWebhook(webhook.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Zapier/Make Info */}
        <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-xl p-6 border border-purple-200 dark:border-purple-800">
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <Zap className="w-5 h-5 text-purple-500" />
            Integracja z Zapier / Make
          </h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-3">
            Użyj webhooka aby automatycznie dodawać leady do Google Sheets, 
            wysyłać SMS-y, tworzyć zadania w Trello i więcej.
          </p>
          <div className="flex gap-2">
            <a 
              href="https://zapier.com/apps/webhook" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-purple-600 hover:underline flex items-center gap-1"
            >
              Zapier Webhooks <ExternalLink className="w-3 h-3" />
            </a>
            <span className="text-zinc-400">•</span>
            <a 
              href="https://www.make.com/en/help/tools/webhooks" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
            >
              Make Webhooks <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>

      {/* Add Webhook Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Dodaj webhook</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1 block">URL webhooka</label>
              <Input
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://hooks.zapier.com/..."
              />
              <p className="text-xs text-zinc-500 mt-1">
                URL na który będziemy wysyłać powiadomienia POST
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Opis (opcjonalnie)</label>
              <Input
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="np. Integracja z moim CRM"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Zdarzenia</label>
              <div className="space-y-2">
                {EVENT_TYPES.map(event => (
                  <label 
                    key={event.id}
                    className="flex items-start gap-3 p-3 rounded-lg border border-zinc-200 dark:border-gray-700 cursor-pointer hover:bg-zinc-50 dark:hover:bg-gray-800"
                  >
                    <Checkbox
                      checked={selectedEvents.includes(event.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedEvents(prev => [...prev, event.id]);
                        } else {
                          setSelectedEvents(prev => prev.filter(e => e !== event.id));
                        }
                      }}
                    />
                    <div>
                      <p className="font-medium text-sm">{event.label}</p>
                      <p className="text-xs text-zinc-500">{event.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Anuluj
            </Button>
            <Button 
              onClick={createWebhook} 
              disabled={saving}
              className="bg-[#F59E0B] hover:bg-[#D97706]"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Utwórz webhook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deliveries Dialog */}
      <Dialog open={showDeliveriesDialog} onOpenChange={setShowDeliveriesDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Historia wysyłek</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {deliveries.length === 0 ? (
              <p className="text-center text-zinc-500 py-8">Brak historii wysyłek</p>
            ) : (
              <div className="space-y-3">
                {deliveries.map((delivery) => (
                  <div 
                    key={delivery.id}
                    className={`p-3 rounded-lg border ${
                      delivery.success 
                        ? 'border-green-200 bg-green-50 dark:bg-green-900/20' 
                        : 'border-red-200 bg-red-50 dark:bg-red-900/20'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Badge className={delivery.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {delivery.success ? 'OK' : 'Błąd'}
                      </Badge>
                      <span className="text-xs text-zinc-500">
                        {new Date(delivery.created_at).toLocaleString('pl-PL')}
                      </span>
                    </div>
                    <p className="text-sm font-medium">{delivery.event}</p>
                    {delivery.status_code && (
                      <p className="text-xs text-zinc-500">
                        Status: {delivery.status_code} • {delivery.duration_ms}ms
                      </p>
                    )}
                    {delivery.error && (
                      <p className="text-xs text-red-600 mt-1">{delivery.error}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </PanelLayout>
  );
}
